<fuse-loading-bar></fuse-loading-bar>

<div class="layout-main layout-main-root flex w-full min-w-0 flex-auto flex-col">
    <mat-drawer-container class="flex-auto layout-main-drawer" [hasBackdrop]="false">
        <mat-drawer-content class="flex flex-col layout-main-content">
            <!--
                Three-zone top bar (modern layout).

                  [ Left   ] logo, left-anchored
                  [ Center ] uniform cluster: nav + utility icons + user
                  [ Right  ] stacked clock (weekday/date on top, big bold time below)

                The centered cluster collapses every nav item and every
                utility icon button to one shared 38 × 38 footprint with
                a 20 px glyph — see `.topbar-unified-cluster` in
                layout.component.scss. The clock has its own dedicated
                slot on the right so it can be sized prominently
                (bigger / bolder) without inflating the cluster.

                Solid slate top bar in dark mode (matches orders workspace
                ambient). Was: bg-card + dark:bg-transparent — that let
                the old Fuse tint show through.
            -->
            <div
                class="layout-topbar-unified relative z-49 flex h-16 w-full flex-0 items-center px-4 sm:h-20 md:px-6 lg:pr-0 print:hidden"
            >
                @if (isScreenSmall) {
                    <!-- Small screens keep the original hamburger + right-edge
                         utilities layout — no centered cluster at this width. -->
                    <button
                        mat-icon-button
                        (click)="toggleNavigation('mainNavigation')"
                    >
                        <mat-icon [svgIcon]="'heroicons_outline:bars-3'"></mat-icon>
                    </button>
                    <div class="ml-auto flex items-center justify-end space-x-0.5 pl-2 sm:space-x-2">
                        <!-- TEMP: Add user button hidden until we find a better
                             home for it. Restore by removing this comment wrap. -->
                        <!-- <add-user-button></add-user-button> -->
                        <fuse-fullscreen class="hidden md:block"></fuse-fullscreen>
                        <voice-calls></voice-calls>
                        <!-- TEMP: Video call button hidden until the feature is
                             working well. Restore by removing this comment wrap. -->
                        <!-- <video-calls></video-calls> -->
                        <messages></messages>
                        <timeline-activities></timeline-activities>
                        <updates-required></updates-required>
                        @if (agentEnabled) {
                        <button mat-icon-button (click)="mcpDrawer.toggle()" [matTooltip]="'AI Assistant'">
                            <mat-icon>auto_awesome</mat-icon>
                        </button>
                        }
                        <button
                            class="lg:hidden"
                            mat-icon-button
                            (click)="quickChat.toggle()"
                        >
                            <mat-icon
                                [svgIcon]="'heroicons_outline:chat-bubble-left-right'"
                            ></mat-icon>
                        </button>
                        <user></user>
                    </div>
                } @else {
                    <!--
                        Five-zone top bar (modern layout).

                          [ 1 Logo ]
                          [ 2 Menu dropdown ]      → Orders / Products / CRM / Users / Full
                          [ 3 Live feed strip ]    → Updates · Activity · Inbox pills (rotating)
                          [ 4 Calls pill ]         → status-aware voice-calls trigger
                          [ 5 Utility cluster ]    → Add user · Account · (AI · Chat)
                          [ 6 Clock ]

                        Why this layout:
                          • The primary nav items the user actually
                            navigates between (Orders / Products / CRM /
                            Users / Full) collapsed into a single
                            dropdown — they used to fan out across the
                            bar but were rarely the click target compared
                            to the live feeds beside them.
                          • Updates / Activity / Inbox icon buttons used
                            to live next to Add/Account. They've been
                            replaced by `<topbar-live-feed>` — three
                            rotating banner pills that surface the
                            *contents* of those feeds instead of just an
                            unread badge.
                          • Voice calls remain a one-click trigger but
                            now read as a pill instead of a circular
                            icon button so the whole "feed strip + calls"
                            row reads as one consistent control set.
                    -->

                    <!--
                        Zone 1 — page-aware menu trigger.
                        Visually the primary nav button (formerly
                        "ORDERS"). It always reflects whichever page
                        the user is on (icon + label come from the
                        matching `navigation.horizontal` entry) and
                        clicking it opens a dropdown with the OTHER
                        options — same role-filtered list the
                        original fanned-out nav showed.

                        A small chevron on the right is the affordance
                        that there's a menu behind the button (so the
                        primary click target still feels like "current
                        page" rather than a hamburger).

                        Logo previously occupied the left slot but has
                        been moved to Zone 6 (right edge, next to the
                        clock) per user request — the menu button is
                        now the leftmost anchor.
                    -->
                    <!--
                        `ml-2` (8 px) nudges the menu trigger to the right
                        so its left edge sits at 32 px from the screen
                        edge — the same horizontal position as the
                        Mailbox workspace chip in the orders list sidebar
                        (drawer's inner content starts at
                        `--orders-rail-gutter: 2rem`). The topbar's own
                        `md:px-6` (24 px) padding only gets us to 24 px,
                        so this extra 8 px finishes the alignment.
                    -->
                    <div class="topbar-menu-trigger flex items-center flex-shrink-0 ml-2">
                        <button
                            type="button"
                            class="topbar-menu-btn topbar-menu-btn--page"
                            [matMenuTriggerFor]="topbarMainMenu"
                            [matTooltip]="menuTooltip"
                        >
                            <mat-icon
                                class="topbar-menu-btn__icon"
                                [svgIcon]="currentNavItem?.icon"
                            ></mat-icon>
                            <span class="topbar-menu-btn__label">
                                {{ currentNavItem?.title || 'Menu' }}
                            </span>
                            <mat-icon class="topbar-menu-btn__chev">expand_more</mat-icon>
                        </button>
                        <!-- Subtle company chip when assisting — page label stays on the trigger. -->
                        <span
                            *ngIf="canUseCompanyPicker && assistCompany"
                            class="topbar-assist-chip"
                            [matTooltip]="'Assisting ' + assistCompanyLabel"
                        >
                            {{ assistCompanyLabel }}
                        </span>
                        <mat-menu #topbarMainMenu="matMenu" class="topbar-main-menu">
                            <!-- Company picker (Admin / Production): above page nav. -->
                            <ng-container *ngIf="canUseCompanyPicker">
                                <div class="topbar-main-menu__section-label">
                                    Company
                                </div>
                                <button
                                    mat-menu-item
                                    type="button"
                                    class="topbar-company-item"
                                    [class.topbar-main-menu__active]="!assistCompany"
                                    (click)="onSelectAssistCompany(null)"
                                >
                                    <span class="topbar-company-item__logo">
                                        <span>{{ initials(selfCompanyLabel) }}</span>
                                        <img [src]="selfCompanyLogo" [alt]="selfCompanyLabel" />
                                    </span>
                                    <span class="topbar-company-item__body">
                                        <span class="topbar-company-item__name">{{ selfCompanyLabel }}</span>
                                        <span class="topbar-company-item__identity">
                                            <span class="topbar-company-item__profile">
                                                <span>{{ initials(selfUserName) }}</span>
                                                <img
                                                    *ngIf="selfProfileImage"
                                                    [src]="selfProfileImage"
                                                    [alt]="selfUserName"
                                                />
                                            </span>
                                            <span class="topbar-company-item__person">{{ selfUserName }}</span>
                                            <span class="topbar-company-item__role">{{ selfUserRole }}</span>
                                        </span>
                                    </span>
                                </button>
                                <button
                                    *ngFor="let company of assistCompanies"
                                    mat-menu-item
                                    type="button"
                                    class="topbar-company-item"
                                    [class.topbar-main-menu__active]="isAssistCompanyActive(company)"
                                    (click)="onSelectAssistCompany(company)"
                                >
                                    <span class="topbar-company-item__logo">
                                        <span>{{ initials(companyMenuLabel(company)) }}</span>
                                        <img
                                            *ngIf="company.company_logo"
                                            [src]="company.company_logo"
                                            [alt]="companyMenuLabel(company)"
                                        />
                                    </span>
                                    <span class="topbar-company-item__body">
                                        <span class="topbar-company-item__heading">
                                            <span class="topbar-company-item__name">{{ companyMenuLabel(company) }}</span>
                                            <mat-icon
                                                class="topbar-company-item__mail"
                                                [class.topbar-company-item__mail--linked]="company.email_token"
                                                [class.topbar-company-item__mail--missing]="!company.email_token"
                                                [matTooltip]="company.email_token ? 'Mailbox connected' : 'No mailbox connected'"
                                            >
                                                {{ company.email_token ? 'mail' : 'mail_off' }}
                                            </mat-icon>
                                        </span>
                                        <span class="topbar-company-item__identity">
                                            <span class="topbar-company-item__profile">
                                                <span>{{ initials(companyUserName(company)) }}</span>
                                                <img
                                                    *ngIf="companyProfileImage(company)"
                                                    [src]="companyProfileImage(company)"
                                                    [alt]="companyUserName(company)"
                                                />
                                            </span>
                                            <span class="topbar-company-item__person">{{ companyUserName(company) }}</span>
                                            <span class="topbar-company-item__role">{{ companyUserRole(company) }}</span>
                                        </span>
                                    </span>
                                </button>
                                <mat-divider></mat-divider>
                            </ng-container>

                            <div class="topbar-main-menu__section-label">
                                Navigation
                            </div>
                            <ng-container *ngFor="let item of (navigation?.horizontal || [])">
                                <a
                                    *ngIf="item?.link"
                                    mat-menu-item
                                    [routerLink]="item.link"
                                    [queryParams]="item.queryParams || null"
                                    [class.topbar-main-menu__active]="isActiveNavItem(item)"
                                >
                                    <mat-icon *ngIf="item.icon" [svgIcon]="item.icon"></mat-icon>
                                    <span>{{ item.title }}</span>
                                </a>
                            </ng-container>
                            <mat-divider></mat-divider>
                            <button
                                mat-menu-item
                                type="button"
                                (click)="fullscreenRef.toggleFullscreen()"
                            >
                                <mat-icon [svgIcon]="'heroicons_outline:arrows-pointing-out'"></mat-icon>
                                <span>Full Screen</span>
                            </button>
                        </mat-menu>

                        <!-- The actual fullscreen widget is rendered
                             off-screen so we can call its toggle method
                             from the menu without dragging its own icon
                             button into the visible nav. -->
                        <fuse-fullscreen
                            #fullscreenRef="fuseFullscreen"
                            class="sr-only-fullscreen"
                            aria-hidden="true"
                        ></fuse-fullscreen>
                    </div>

                    <!--
                        Zone 3 — live feed strip (the heart of the nav).
                        Three rotating banner pills showing Updates
                        Required, Activity, and Messages. Each pill is
                        a click target that routes into the matching
                        workspace surface.

                        `ml-8` (2 rem = 32 px) — chosen so the ticker's
                        left edge starts at 256 px from the screen edge,
                        which is exactly where the orders cards area
                        begins (right after the 16 rem filter sidebar).
                        Computation:
                          24 (topbar pl) + 8 (menu trigger ml) + 192
                          (menu btn width 12rem) + 32 (this ml) = 256
                        So the ticker visually continues the cards area
                        upward into the topbar.
                    -->
                    <div class="topbar-zone-feed flex flex-1 items-center justify-center min-w-0 ml-8 mr-3">
                        <topbar-live-feed></topbar-live-feed>
                    </div>

                    <!-- Zone 4 — calls pill (status-aware). -->
                    <div class="topbar-calls-pill flex items-center flex-shrink-0 gap-2">
                        <voice-calls></voice-calls>
                        <!-- TEMP: Video call button hidden until the feature is
                             working well. Restore by removing this comment wrap. -->
                        <!-- <video-calls></video-calls> -->
                    </div>

                    <!--
                        Zone 5 — remaining utility actions
                        (AI Assistant + small-screen chat shortcut).
                        "Add" and "Account" have moved out of this
                        cluster: Add is TEMP-hidden, and Account is now
                        the standalone profile slot at the far right
                        (zone 7) per user request.
                    -->
                    <div class="topbar-named-cluster flex items-center flex-shrink-0 ml-2">
                        <!-- TEMP: "Add user" named-action hidden until we find
                             a better place for it. Restore by removing this
                             comment wrap. -->
                        <!--
                        <div class="topbar-named-action">
                            <add-user-button></add-user-button>
                            <span class="topbar-named-action__label">Add</span>
                        </div>
                        -->

                        <!-- AI Assistant (conditional). -->
                        @if (agentEnabled) {
                            <div class="topbar-named-action">
                                <button
                                    mat-icon-button
                                    (click)="mcpDrawer.toggle()"
                                    [matTooltip]="'AI Assistant'"
                                >
                                    <mat-icon>auto_awesome</mat-icon>
                                </button>
                                <span class="topbar-named-action__label">AI</span>
                            </div>
                        }

                        <!-- Chat shortcut on small screens (the chat
                             surface is normally reached from the orders
                             workspace; this is the fallback). -->
                        <div class="topbar-named-action lg:hidden">
                            <button
                                mat-icon-button
                                (click)="quickChat.toggle()"
                            >
                                <mat-icon
                                    [svgIcon]="'heroicons_outline:chat-bubble-left-right'"
                                ></mat-icon>
                            </button>
                            <span class="topbar-named-action__label">Chat</span>
                        </div>
                    </div>

                    <!--
                        Zone 6 — branded clock card (logo + stacked
                        clock fused into a single slate plate).

                        The logo sits inside the SAME container as the
                        date/time per user request — visually they read
                        as one branded chip rather than two adjacent
                        pieces. The slate plate styling (background,
                        border, rounding, padding) is owned by the
                        outer `.topbar-clock-stack`; the logo image
                        rides on the left, the date/time stack on the
                        right.
                    -->
                    <div class="topbar-zone-right flex items-center justify-end flex-shrink-0 ml-2 lg:mr-3">
                        <div class="topbar-clock-stack hidden lg:flex items-center gap-3">
                            <div class="topbar-clock-stack__logo flex items-center">
                                <img class="object-contain" [src]="logoUrl" [alt]="logoAlt" />
                            </div>
                            <div class="topbar-clock-stack__text flex flex-col items-start leading-none">
                                <span class="topbar-clock-date">{{ currentTopbarDate }}</span>
                                <span class="topbar-clock-time tabular-nums">{{ currentTopbarTime }}</span>
                            </div>
                        </div>
                    </div>

                    <!--
                        Zone 7 — profile button, centered above the
                        quick-chat rail. The rail (`<quick-chat>`) is a
                        fixed `lg:w-16` (4 rem = 64 px) vertical strip
                        anchored to the right edge of the viewport. This
                        slot is the same 4 rem width and is the LAST
                        element in the topbar (with `lg:pr-0` on the
                        outer container) so it sits flush against the
                        right edge — its horizontal center visually
                        aligns with the rail's center directly below.

                        Label intentionally omitted (was "Account"); the
                        oversized avatar / user icon carries the meaning
                        on its own.
                    -->
                    <div class="topbar-zone-profile hidden lg:flex items-center justify-center flex-shrink-0 w-[var(--quick-chat-rail-width)]">
                        <user></user>
                    </div>

                    <!--
                        Sub-lg fallback — on screens narrower than `lg`
                        the chat rail collapses, so the profile button
                        rides inline at the end of the bar instead of
                        in its own 4 rem slot.
                    -->
                    <div class="topbar-zone-profile-inline flex lg:hidden items-center flex-shrink-0 ml-2 mr-1">
                        <user></user>
                    </div>
                }
            </div>

            <div
                class="layout-page-shell layout-main-outlet flex w-full flex-auto flex-col lg:pr-[var(--quick-chat-rail-width)]"
            >
                <div class="layout-page-ambient" aria-hidden="true">
                    <div class="layout-page-ambient__wash"></div>
                    <workspace-particles
                        [density]="0.55"
                        [dotSize]="3"
                        color="#94a3b8"
                        [maxSpeed]="11"
                        [minAlpha]="0.10"
                        [maxAlpha]="0.45"
                    ></workspace-particles>
                </div>
                <div class="layout-page-content flex w-full flex-auto flex-col min-h-0">
                    @if (true) {
                        <router-outlet></router-outlet>
                    }
                </div>
            </div>
            <!--<div
                class="bg-card relative z-49 flex h-14 w-full flex-0 items-center border-t px-4 dark:bg-transparent sm:h-20 md:px-6 print:hidden"
            >
                <span class="text-secondary font-medium"
                    >District Printing &copy; {{ currentYear }}</span
                >
            </div>-->
        </mat-drawer-content>

        <mat-drawer #profileDrawer class="w-full md:w-160" [mode]="'over'" [position]="'start'">
            <div class="flex flex-col h-full overflow-hidden">
                <div class="flex items-center justify-between flex-shrink-0 px-8 pt-8 pb-4">
                    <h2 class="text-2xl font-semibold">My Profile</h2>
                    <button mat-icon-button (click)="profileDrawer.close()">
                        <mat-icon [svgIcon]="'heroicons_outline:x-mark'"></mat-icon>
                    </button>
                </div>
                <div class="flex-1 min-h-0 overflow-hidden">
                    @defer (on timer(1s)) {
                        <quick-add-user 
                            [editMode]="true" 
                            [userId]="null"
                            (closeDrawer)="profileDrawer.close()">
                        </quick-add-user>
                    }
                </div>
            </div>
        </mat-drawer>

    </mat-drawer-container>
</div>

@if (isScreenSmall) {
    <fuse-vertical-navigation
        class="dark bg-gray-900 print:hidden"
        [mode]="'over'"
        [name]="'mainNavigation'"
        [navigation]="navigation.default"
        [opened]="false"
    >
        <ng-container fuseVerticalNavigationContentHeader>
            <div class="flex h-24 items-center px-8 pt-6">
                <img
                    class="w-24 max-h-20 object-contain"
                    [src]="logoUrl"
                    [alt]="logoAlt"
                />
            </div>
        </ng-container>
    </fuse-vertical-navigation>
}

<quick-chat #quickChat="quickChat"></quick-chat>
<mcp-drawer #mcpDrawer></mcp-drawer>

<!--
    Company-assist watermark — a constant, full-screen reminder of the
    company and account owner the operator is currently assisting.
-->
<div
    *ngIf="canUseCompanyPicker && assistCompany"
    class="assist-watermark"
    aria-hidden="true"
>
    <div class="assist-watermark__identity">
        <span class="assist-watermark__details">
            <span class="assist-watermark__eyebrow">Assisting user</span>
            <span class="assist-watermark__user">
                <span class="assist-watermark__user-name">
                    {{ companyUserName(assistCompany) }}
                </span>
                <span class="assist-watermark__role">
                    {{ companyUserRole(assistCompany) }}
                </span>
            </span>
            <span class="assist-watermark__company">{{ assistCompanyLabel }}</span>
        </span>
    </div>
</div>